import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, Collapse, Paper, SwipeableDrawer, Tooltip, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { alpha } from "@mui/material/styles";
import type { DateTime } from "luxon";
import { type FC, type MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { type UserNurseInfo, useAuth } from "@/contexts/AuthProvider";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useRequestsContext } from "@/contexts/RequestsContext";
import { useDepartmentColor } from "@/hooks/useDepartmentColor";
import { useGetResolverJobDisplayName } from "@/hooks/useJobDisplayName";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { useCancelPickedShifts } from "@/hooks/useOpenShiftActions";
import { usePickShift } from "@/hooks/usePickShift";
import { useBedsideNurse } from "@/pages/BedsideHome/hooks";
import { ShiftTypeIcon } from "@/pages/MasterSchedule/v2/auxillary/ShiftTypeIcon";
import { trackErrorEvent, trackEvent } from "@/utils/analytics";
import { formatHoursMinutes, getShiftDisplayDate } from "@/utils/dateUtils";
import { canMergeBonus, findNextWindow, sortedCurveWindows } from "@/utils/incentive";
import {
	type Nurse,
	NurseFullRoleShort,
	type OpenShiftType,
	type PickShiftResponse,
	RequestResponsesStatusOptions,
	RequestStatus,
	type ShiftRequest,
} from "@/utils/models";
import { CustomCalendar } from "../CustomCalendar/CustomCalendar";
import { EditTimePopover } from "../EditTimePopover";
import { HeaderButton } from "../headerButton/HeaderButton";
import { IncentiveCurveBreakdown } from "../IncentiveCurveBreakdown/IncentiveCurveBreakdown";
import { CancelShiftDialog } from "./CancelShiftDialog";
import { PickShiftSuccessOverlay } from "./PickShiftSuccessOverlay";
import { StatusBadge } from "./StatusBadge";

export type OpenShiftCardProps = {
	nurse?: Nurse;
	shift: OpenShiftType;
	isExpanded: boolean;
	onExpandClick: (id: string) => void;
	isHighlighted?: boolean;
	id?: string;
	dateTitle?: boolean;
};

export const OpenShiftCard: FC<OpenShiftCardProps> = ({ shift, isExpanded, onExpandClick, isHighlighted = false, id, nurse, dateTitle }) => {
	const { t } = useTranslation();
	const { userInfo } = useAuth();
	const { transformedShifts, showErrorSnackbar, timezone, getDepartmentDisplayName, chosenDepartment } = useHospitalData();
	const getJobDisplayName = useGetResolverJobDisplayName();
	const departmentColor = useDepartmentColor();
	const { userShifts } = useBedsideNurse();
	const { staffRequests, setStaffRequests, refreshRequests } = useRequestsContext();
	const { mutateAsync: cancelPickedShifts, isPending: isCanceling } = useCancelPickedShifts();
	const { pickShift, isPicking } = usePickShift();
	const isPartialPickupEnabled = useFeatureFlag(FEATURE_FLAGS.PartialPickup);
	const isPickupByLocation = useFeatureFlag(FEATURE_FLAGS.PickupByLocation);
	const displayBonus = shift.current_bonus ?? shift.bonus;
	const nextWindow = findNextWindow(shift.incentive_windows, shift.posted_at);
	const sortedWindows = sortedCurveWindows(shift.incentive_windows);
	const [partialMode, setPartialMode] = useState<boolean>(false);
	const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement>();
	const isLoading = isCanceling || isPicking;

	const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
	const [containedRequests, setContainedRequests] = useState<any[]>([]);
	const [pendingCancelShift, setPendingCancelShift] = useState<OpenShiftType | null>(null);
	const [pickSuccessOverlayOpen, setPickSuccessOverlayOpen] = useState(false);

	const spansDepartments = new Set(userInfo.nurses_info.flatMap((nurseInfo) => nurseInfo.departments)).size > 1;
	const showDepartment = spansDepartments || userInfo.nurses_info.length > 1 || !userInfo.group_floatpool_shifts;
	// The role is shown whenever the shift has one, so single-job staff also see which role they'd pick up.
	const jobName = shift.job_id ? getJobDisplayName(shift.job_id) : undefined;

	const handleExpandClick = (e: MouseEvent): void => {
		e.stopPropagation();
		onExpandClick(shift.id);

		trackEvent("open_shift_card_expanded", {
			shift_id: shift.id,
			shift_type: shift.type,
			date: shift.startTime.toFormat("yyyy-MM-dd"),
			is_expanding: !isExpanded,
		});
	};
	// Helper function to find contained shift IDs
	const getContainedShiftIds = (shift: OpenShiftType, nurseInfo: UserNurseInfo): string[] => {
		const myPickedRequests = staffRequests.filter(
			(req: ShiftRequest) =>
				req.status === RequestStatus.Active &&
				req.message.request_data.some((data) => data.nurse_id === nurseInfo.nurse.id && data.status === RequestResponsesStatusOptions.PENDING),
		);

		// Get the main shift's picked time range
		const mainShiftData = myPickedRequests.find((req: ShiftRequest) => req.id === shift.id);
		const mainShiftPickData = mainShiftData?.message.request_data.find(
			(d) => d.nurse_id === nurseInfo.nurse.id && d.status === RequestResponsesStatusOptions.PENDING,
		);
		const acceptedStartTime = mainShiftPickData?.accepted_start_time;
		const acceptedEndTime = mainShiftPickData?.accepted_end_time;

		if (!acceptedStartTime || !acceptedEndTime) return [];

		// Find contained shifts
		const containedIds: string[] = [];
		for (const req of myPickedRequests as ShiftRequest[]) {
			if (req.id === shift.id) continue;

			const reqStart = req.message.shift_start;
			const reqEnd = req.message.shift_end;

			if (
				reqStart >= acceptedStartTime &&
				reqEnd <= acceptedEndTime &&
				req.department_id === shift.department_id &&
				req.charge_only === shift.charge_only &&
				canMergeBonus(req.incentive_windows, shift.incentive_windows) &&
				req.job_id === shift.job_id
			) {
				containedIds.push(req.id);
			}
		}

		return containedIds;
	};

	const handleCancelPickShifts = async (shift: OpenShiftType): Promise<void> => {
		try {
			const nurseInfo: UserNurseInfo | undefined = userInfo.nurses_info.find((n) => n.departments.includes(shift.department_id));
			if (!nurseInfo) return;

			const containedIds = getContainedShiftIds(shift as OpenShiftType, nurseInfo);

			if (containedIds.length > 0) {
				const containedShiftsData = staffRequests
					.filter((req: ShiftRequest) => containedIds.includes(req.id))
					.map((s: ShiftRequest) => ({
						request_id: s.id,
						shift_start: s.message.shift_start,
						shift_end: s.message.shift_end,
						shift_type: s.message.shift_type,
						roles: s.message.roles,
					}));

				setContainedRequests(containedShiftsData);
				setPendingCancelShift(shift as OpenShiftType);
				setCancelDialogOpen(true);
				return;
			}

			const requestIds = shift.original_shifts?.map((s) => s.id) || [shift.id];
			await cancelShifts(requestIds, shift, nurseInfo);
		} catch (error: any) {
			showErrorSnackbar(error.response?.data?.detail?.message || error?.message, error);
			trackErrorEvent("open_shift_card_cancel_pick_failed", error, {
				shift_id: shift.id,
				is_merged: shift.is_merged || false,
			});
		}
	};

	const handleDialogConfirm = async (cancelContained: boolean) => {
		setCancelDialogOpen(false);

		if (!pendingCancelShift) return;

		const nurseInfo = userInfo.nurses_info.find((n) => n.departments.includes(pendingCancelShift.department_id));
		if (!nurseInfo) return;

		const mainIds = pendingCancelShift.original_shifts?.map((s: { id: string }) => s.id) || [pendingCancelShift.id];
		const containedIds = containedRequests.map((s: { request_id: string }) => s.request_id);
		const finalIds = cancelContained ? [...mainIds, ...containedIds] : mainIds;

		try {
			trackEvent("open_shift_card_cancel_pick", {
				request_ids: finalIds,
				is_merged: pendingCancelShift.is_merged || false,
				shift_date: pendingCancelShift.startTime.toFormat("yyyy-MM-dd"),
				shift_type: pendingCancelShift.type,
			});
			await cancelShifts(finalIds, pendingCancelShift, nurseInfo);
		} catch (error: any) {
			showErrorSnackbar(error.response?.data?.detail?.message || error?.message, error);
			trackErrorEvent("open_shift_card_cancel_pick_failed", error, {
				shift_id: shift.id,
				is_merged: shift.is_merged || false,
			});
		}

		setPendingCancelShift(null);
		setContainedRequests([]);
	};

	const cancelShifts = async (requestIds: string[], shift: OpenShiftType, nurseInfo: UserNurseInfo) => {
		trackEvent("open_shift_card_cancel_pick", {
			request_ids: requestIds,
			is_merged: shift.is_merged || false,
			shift_date: shift.startTime.toFormat("yyyy-MM-dd"),
			shift_type: shift.type,
		});

		await cancelPickedShifts({
			departmentId: shift.department_id,
			nurseId: nurseInfo.nurse.id,
			requestIds,
		});

		setStaffRequests((prevRequests) =>
			prevRequests.map((req) => {
				if (requestIds.includes(req.id)) {
					req = { ...req };
					req.message.request_data = req.message.request_data.filter((data) => data.nurse_id !== nurseInfo.nurse.id);
				}
				return req;
			}),
		);
		await refreshRequests();

		trackEvent("open_shift_card_cancel_pick_success", { shift_ids: requestIds /* ... */ });
	};

	const handlePickShift = async (shift: OpenShiftType, selectedStart?: DateTime, selectedEnd?: DateTime): Promise<void> => {
		try {
			const windowCount = shift.incentive_windows?.length ?? 0;
			const planType: "flat" | "decay" | "none" = windowCount > 1 ? "decay" : windowCount === 1 ? "flat" : "none";
			const incentivePayload = {
				bonus_amount: displayBonus,
				hours_left: shift.hours_left ?? null,
				plan_type: planType,
			};
			trackEvent(shift.is_merged ? "open_shift_card_pick_merged_shifts" : "open_shift_card_pick_shift", {
				shift_id: shift.id,
				...(shift.is_merged && shift.original_shifts ? { shift_ids: shift.original_shifts.map((s) => s.id) } : {}),
				shift_date: shift.startTime.toFormat("yyyy-MM-dd"),
				shift_type: shift.type,
				shift_duration_hours: shift.endTime.diff(shift.startTime, "hours"),
				accepted_start_time: selectedStart,
				accepted_end_time: selectedEnd,
				...incentivePayload,
			});
			const result = await pickShift({
				id: shift.id,
				departmentId: shift.department_id,
				startTime: selectedStart ?? shift.startTime,
				endTime: selectedEnd ?? shift.endTime,
				isMerged: shift.is_merged,
				originalShifts: shift.original_shifts,
				partial: partialMode,
				bonusAtPickup: displayBonus,
				hoursLeftAtPickup: shift.hours_left,
			});

			setStaffRequests((prevRequests) => {
				const pickedRequests: PickShiftResponse[] = result?.data?.requests_to_pick ?? [];
				const nurseInfo = userInfo.nurses_info.find((n) => n.departments.includes(shift.department_id));

				return prevRequests.map((req) => {
					const pickedRequest = pickedRequests.find((r: PickShiftResponse) => r.request_id === req.id);

					if (pickedRequest && nurseInfo) {
						req.message.request_data.push({
							nurse_id: nurseInfo.nurse.id,
							status: RequestResponsesStatusOptions.PENDING,
							accepted_start_time: pickedRequest.accepted_start_time,
							accepted_end_time: pickedRequest.accepted_end_time,
						});
					}
					return req;
				});
			});

			setPickSuccessOverlayOpen(true);
			trackEvent("open_shift_card_pick_shift_success_shown", {
				shift_id: shift.id,
				is_merged: shift.is_merged || false,
				shift_date: shift.startTime.toFormat("yyyy-MM-dd"),
				shift_type: shift.type,
				...incentivePayload,
			});
		} catch (error) {
			// The pick mutation's onError already surfaces a (parsed) snackbar; only track here to avoid a duplicate toast.
			trackErrorEvent("open_shift_card_pick_failed", error, {
				shift_id: shift.id,
				is_merged: shift.is_merged || false,
				error: error instanceof Error ? error.message : "Unknown error",
				accepted_start_time: selectedStart,
				accepted_end_time: selectedEnd,
			});
		}
	};

	const handlePickPartial = (): void => {
		setPartialMode(true);
		setMenuAnchorEl(undefined);
	};
	const handlePickFull = (): void => {
		setPartialMode(false);
		setMenuAnchorEl(undefined);
	};

	return (
		<>
			<Box
				id={id}
				data-testid={`open-shift-card-${shift.id}`}
				sx={{
					width: "100%",
					mb: 2,
					position: "relative",
					...(isHighlighted && {
						"&::before": {
							content: '""',
							position: "absolute",
							top: -4,
							left: -4,
							right: -4,
							bottom: -4,
							border: "2px solid #3182CE",
							borderRadius: "8px",
							pointerEvents: "none",
							animation: "pulse 2s infinite",
						},
						"@keyframes pulse": {
							"0%": {
								boxShadow: "0 0 0 0 rgba(49, 130, 206, 0.4)",
							},
							"70%": {
								boxShadow: "0 0 0 10px rgba(49, 130, 206, 0)",
							},
							"100%": {
								boxShadow: "0 0 0 0 rgba(49, 130, 206, 0)",
							},
						},
					}),
				}}
			>
				<Paper
					elevation={0}
					sx={{
						display: "flex",
						flexDirection: "column",
						borderRadius: "8px",
						boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
						width: "100%",
						bgcolor: "white",
						position: "relative",
						pb: 0,
						pt: 0.5,
						px: 0.5,
					}}
				>
					<Box
						sx={{
							p: { xs: 1, sm: 2 },
							display: "flex",
							flexDirection: "column",
							gap: 1,
						}}
					>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								width: "100%",
							}}
						>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
								{dateTitle && (
									<Typography sx={{ color: "primary.main" }}>
										{getShiftDisplayDate(shift.startTime, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes).toFormat("ccc, MMM d")}
									</Typography>
								)}

								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: { xs: 1, sm: 1.5 },
									}}
								>
									<ShiftTypeIcon shiftType={shift.type} />
									<Typography
										variant="h6"
										sx={{
											fontSize: { xs: "1rem", sm: "1.25rem" },
											fontWeight: 600,
											color: "primary.dark",
											lineHeight: 1.2,
										}}
									>
										{shift.accepted_start_time ? shift.accepted_start_time.setZone(timezone).toFormat("h:mm a") : shift.startTime.toFormat("h:mm a")} -{" "}
										{shift.accepted_end_time ? shift.accepted_end_time.setZone(timezone).toFormat("h:mm a") : shift.endTime.toFormat("h:mm a")}{" "}
									</Typography>
								</Box>
							</Box>

							<Box sx={{ display: "flex", alignItems: "center" }}>
								{shift.status !== RequestResponsesStatusOptions.DECLINED &&
									shift.status !== RequestResponsesStatusOptions.CLOSED &&
									shift.myStatus !== RequestResponsesStatusOptions.PENDING &&
									shift.myStatus !== RequestResponsesStatusOptions.APPROVED && (
										<>
											{isPartialPickupEnabled && (
												<Menu anchorEl={menuAnchorEl} open={!!menuAnchorEl} onClose={() => setMenuAnchorEl(undefined)}>
													{!partialMode ? (
														<MenuItem data-testid={`open-shift-pick-partial-${shift.id}`} onClick={handlePickPartial}>
															{t("pick.partial")}
														</MenuItem>
													) : (
														<MenuItem data-testid={`open-shift-pick-full-${shift.id}`} onClick={handlePickFull}>
															{t("pick.full.shift")}
														</MenuItem>
													)}
												</Menu>
											)}
											<Box data-testid={`open-shift-claim-${shift.id}`} component="span">
												<HeaderButton
													width={"16vw"}
													maxWidth={"100px"}
													text={t("pick")}
													onPress={() => handlePickShift(shift, shift.startTime, shift.endTime)}
													state="on"
													disabled={isLoading}
												/>
											</Box>
											{isPartialPickupEnabled && (
												<IconButton
													data-testid={`open-shift-more-menu-${shift.id}`}
													onClick={(e: MouseEvent<HTMLElement>) => setMenuAnchorEl(e.currentTarget)}
													size="small"
												>
													<MoreVertIcon />
												</IconButton>
											)}
										</>
									)}
								{shift.myStatus === RequestResponsesStatusOptions.PENDING && (
									<>
										<HeaderButton
											dataTestId={`open-shift-cancel-${shift.id}`}
											text={t("cancel")}
											width={"16vw"}
											maxWidth={"100px"}
											state="on"
											onPress={() => handleCancelPickShifts(shift as OpenShiftType)}
											disabled={isLoading}
										/>
										<IconButton
											data-testid={`open-shift-more-menu-disabled-${shift.id}`}
											onClick={(e: MouseEvent<HTMLElement>) => setMenuAnchorEl(e.currentTarget)}
											size="small"
											disabled
										>
											<MoreVertIcon />
										</IconButton>
									</>
								)}
							</Box>
						</Box>

						{showDepartment && (
							<Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
								<Box
									sx={{
										bgcolor: departmentColor(shift.department_id).dot,
										width: "4px",
										height: jobName ? "50px" : "30px",
										borderRadius: "2px",
										mr: 1,
									}}
								/>
								<Box>
									<Typography
										variant="body2"
										sx={{
											color: (theme) => alpha(theme.palette.primary.dark, 0.5),
											fontSize: "0.875rem",
											fontWeight: 600,
										}}
									>
										{getDepartmentDisplayName(shift.department_id)}
									</Typography>
									{jobName && (
										<Typography
											data-testid={`open-shift-job-${shift.id}`}
											variant="body2"
											sx={{
												color: (theme) => alpha(theme.palette.primary.dark, 0.5),
												fontSize: "0.875rem",
												fontWeight: 600,
											}}
										>
											{jobName}
										</Typography>
									)}
								</Box>
							</Box>
						)}
						{isPickupByLocation && shift.locations && shift.locations.length > 0 && (
							<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, ml: { xs: 4, sm: 4.5 } }}>
								{shift.locations.length === 1 ? (
									<Typography variant="body2" sx={{ color: "primary.dark", fontSize: "0.875rem" }}>
										{shift.locations[0]}
									</Typography>
								) : (
									<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
										{shift.locations.map((loc) => (
											<Typography key={loc} variant="body2" sx={{ color: "primary.dark", fontSize: "0.875rem" }}>
												{loc}
												{loc !== shift.locations![shift.locations!.length - 1] ? "," : ""}
											</Typography>
										))}
									</Box>
								)}
							</Box>
						)}

						{!showDepartment && jobName && (
							<Typography
								data-testid={`open-shift-job-${shift.id}`}
								variant="body2"
								sx={{
									color: (theme) => alpha(theme.palette.primary.dark, 0.5),
									fontSize: "0.875rem",
									fontWeight: 600,
									mb: 0.5,
								}}
							>
								{jobName}
							</Typography>
						)}
						{shift.myStatus === RequestResponsesStatusOptions.PENDING &&
							shift.accepted_start_time &&
							shift.accepted_end_time &&
							(!shift.accepted_start_time.hasSame(shift.startTime, "minute") || !shift.accepted_end_time.hasSame(shift.endTime, "minute")) && (
								<Typography
									variant="body1"
									sx={{
										color: "primary.dark",
										fontWeight: 500,
										ml: { xs: 4, sm: 4.5 },
										fontSize: { xs: "0.875rem", sm: "0.875rem" },
										lineHeight: 1.2,
									}}
								>
									{t("accepted.partial.shift")}
								</Typography>
							)}

						<SwipeableDrawer
							anchor="bottom"
							onOpen={() => {}}
							open={partialMode}
							onClose={() => setPartialMode(false)}
							slotProps={{
								paper: {
									sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16 },
								},
							}}
						>
							<Box sx={{ p: 3, pb: 4, width: "100%" }}>
								<IconButton
									data-testid={`open-shift-partial-close-${shift.id}`}
									aria-label="close"
									onClick={() => setPartialMode(false)}
									sx={{ position: "absolute", top: 8, right: 8 }}
									size="large"
								>
									<CloseIcon />
								</IconButton>
								<EditTimePopover
									startTime={shift.startTime.setZone(timezone)}
									endTime={shift.endTime.setZone(timezone)}
									minStartTime={shift.startTime.setZone(timezone)}
									maxEndTime={shift.endTime.setZone(timezone)}
									onTimeChange={({ newStartTime, newEndTime }) => handlePickShift(shift, newStartTime, newEndTime)}
									onClose={() => setPartialMode(false)}
									anchorEl={null}
									isActual={false}
									role={nurse?.role && NurseFullRoleShort[nurse.role]}
									jobId={shift.job_id}
									menuWrapper={false}
									fullsize
								/>
							</Box>
						</SwipeableDrawer>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
							{shift.status && <StatusBadge status={shift.status.toLowerCase()} />}
							{!!displayBonus && (
								<Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 1, flexWrap: "wrap" }}>
									<Box
										sx={{
											display: "inline-flex",
											alignItems: "center",
											gap: 0.5,
											bgcolor: "var(--inhouse-secondary)",
											borderRadius: "16px",
											px: 1.5,
										}}
									>
										{sortedWindows ? (
											<Tooltip arrow title={<IncentiveCurveBreakdown windows={sortedWindows} postedAt={shift.posted_at} hoursLeft={shift.hours_left} />}>
												<img src="/bonus.svg" alt="bonus" width={12} style={{ cursor: "pointer" }} />
											</Tooltip>
										) : (
											<img src="/bonus.svg" alt="bonus" width={12} />
										)}
										<Typography
											sx={{
												color: "white",
												fontWeight: "bold",
												fontSize: "0.875rem",
											}}
										>
											+${displayBonus} {t("pickup.bonus")}
										</Typography>
									</Box>
									{shift.hours_left != null && (
										<Box
											sx={{
												display: "inline-flex",
												alignItems: "center",
												gap: 0.5,
												bgcolor: "#FFEDD5",
												color: "#9A3412",
												borderRadius: "16px",
												px: 1.5,
												fontSize: "0.75rem",
												fontWeight: "bold",
											}}
										>
											<AccessTimeIcon sx={{ fontSize: "0.875rem" }} />
											{t("incentive.hours.left.short", { duration: formatHoursMinutes(shift.hours_left) })}
											{nextWindow != null && <span>{` → $${nextWindow.value}`}</span>}
										</Box>
									)}
								</Box>
							)}
						</Box>
					</Box>
					<Box
						sx={{
							width: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							py: 0.5,
							mt: -0.5,
							cursor: "pointer",
							position: "relative",
							"&:after": isExpanded
								? {
										content: '""',
										position: "absolute",
										left: "20%",
										right: "20%",
										bottom: 0,
										height: "1px",
										bgcolor: "transparent",
									}
								: {},
						}}
						data-testid={`open-shift-expand-${shift.id}`}
						onClick={handleExpandClick}
					>
						<Box
							sx={{
								width: 20,
								height: 20,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<KeyboardArrowDownIcon
								sx={{
									color: "#666666",
									transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
									transition: "transform 0.3s ease",
									fontSize: 20,
								}}
							/>
						</Box>
					</Box>

					<Collapse in={isExpanded}>
						<Box
							sx={{
								bgcolor: "white",
								borderTop: "1px solid #E0E0E0",
								position: "relative",
								zIndex: 0,
							}}
						>
							<CustomCalendar shifts={nurse ? userShifts : transformedShifts} value={shift.startTime.startOf("day")} isOpen={isExpanded} showOneWeek={true} />
						</Box>
					</Collapse>
				</Paper>
			</Box>

			<CancelShiftDialog
				open={cancelDialogOpen}
				onClose={() => {
					setCancelDialogOpen(false);
					setPendingCancelShift(null);
					setContainedRequests([]);
				}}
				onConfirm={handleDialogConfirm}
			/>

			<PickShiftSuccessOverlay open={pickSuccessOverlayOpen} onClose={() => setPickSuccessOverlayOpen(false)} />
		</>
	);
};
